import { toast } from 'sonner';

/**
 * Marker set by the httpClient response interceptor when it has already
 * shown a toast for this error, so catch-site `toastError` calls don't
 * show a duplicate for the same failure.
 */
const TOAST_SHOWN_FLAG = '__globalErrorToastShown';

export function markErrorToastShown(err: unknown): void {
  if (err && typeof err === 'object') {
    (err as Record<string, unknown>)[TOAST_SHOWN_FLAG] = true;
  }
}

export function wasErrorToastShown(err: unknown): boolean {
  return Boolean(
    err &&
      typeof err === 'object' &&
      (err as Record<string, unknown>)[TOAST_SHOWN_FLAG],
  );
}

/**
 * Extract a human-readable message from an unknown caught value.
 *
 * Handles plain strings (many call sites `throw data.error`), axios errors
 * (`response.data.error`), Supabase PostgrestError-shaped objects
 * (`{ code, details, hint, message }`), and Error instances. The API payload
 * is checked BEFORE `err.message` because AxiosError is an Error subclass
 * whose own message is just "Request failed with status code N" — the useful
 * message lives in `response.data`.
 */
export function getErrorMessage(err: unknown): string {
  if (typeof err === 'string' && err.trim()) return err;
  if (err && typeof err === 'object') {
    const anyErr = err as {
      response?: { data?: { error?: unknown; message?: unknown } };
      error?: unknown;
      message?: unknown;
    };
    const candidates = [
      anyErr.response?.data?.error,
      anyErr.response?.data?.message,
      anyErr.error,
      anyErr.message,
    ];
    for (const candidate of candidates) {
      if (typeof candidate === 'string' && candidate.trim()) return candidate;
      if (
        candidate &&
        typeof candidate === 'object' &&
        typeof (candidate as { message?: unknown }).message === 'string'
      ) {
        return (candidate as { message: string }).message;
      }
    }
  }
  return 'Something went wrong. Please try again.';
}

/**
 * Safe replacement for casting a caught value straight into `toast.error`
 * (the old `... as string` pattern): rendering a non-string
 * (Error / PostgrestError object) as a toast child crashes React with
 * minified error #31, which was a top error-tracking issue in PostHog.
 *
 * No-ops when the httpClient interceptor already toasted this error.
 */
export function toastError(err: unknown): void {
  if (wasErrorToastShown(err)) return;
  toast.error(getErrorMessage(err));
}
